import { useState, useEffect, useContext } from "react";
import apiClient from "../../services/apiClient";
import {
  Form,
  Input,
  Button,
  Card,
  Typography,
  Collapse,
  Checkbox,
  Row,
  Col,
  message,
  Space,
  Divider,
} from "antd";
import {
  UserAddOutlined,
  PlusOutlined,
  CheckOutlined,
} from "@ant-design/icons";
import "./AddUser.css";
import { ThemeContext } from "../../context/ThemeContext";
import RoleModal from "./RoleModal";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;
const { Panel } = Collapse;

const PERMISSION_GROUPS = [
  { key: "rating", label: "Rating" },
  { key: "financial", label: "Financial" },
  { key: "issuer", label: "Issuer" },
  { key: "migration", label: "Migration" },
  { key: "news", label: "News" },
  { key: "user", label: "User Management" },
];
const PERMISSIONS = [
  { key: "read", label: "Read" },
  { key: "add", label: "Add" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
];

const AddUser = () => {
  const [form] = Form.useForm();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customRoleName, setCustomRoleName] = useState("");
  const [customRoleDesc, setCustomRoleDesc] = useState("");
  const [customPermissions, setCustomPermissions] = useState({});
  const [customRoles, setCustomRoles] = useState([]);
  const [availablePolicies, setAvailablePolicies] = useState(null); // For dynamic policies
  const [selectedPolicyIds, setSelectedPolicyIds] = useState([]); // For checked policies
  // Helper to get all policy IDs for a group
  const getGroupPolicyIds = (group) =>
    availablePolicies && availablePolicies[group]
      ? availablePolicies[group].map((policy) => policy.id)
      : [];

  // Handler to merge checked values from all groups
  const handlePolicyGroupChange = (group, checked) => {
    let allChecked = new Set(selectedPolicyIds);
    // Remove all current group's policy IDs
    getGroupPolicyIds(group).forEach((id) => allChecked.delete(id));
    // Add the new checked values for this group
    checked.forEach((id) => allChecked.add(id));
    setSelectedPolicyIds(Array.from(allChecked));
  };
  const [customRoleLoading, setCustomRoleLoading] = useState(false);
  const { isDark } = useContext(ThemeContext);
  const navigate = useNavigate();

  // Fetch roles from API
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const res = await apiClient.get(`${baseUrl}/roles`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });

      // Check if response has the expected structure with success field
      if (!res.data.success) {
        throw new Error("Failed to fetch roles - success field is false");
      }

      setRoles(res.data.data);
    } catch (e) {
      setRoles([
        { id: 1, name: "Admin", description: "Full access to all features" },
        { id: 2, name: "User", description: "Standard user access" },
        {
          id: 3,
          name: "Manager",
          description: "Can manage users and view reports",
        },
        {
          id: 4,
          name: "Operation",
          description: "Handles operational tasks and workflows",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch roles on mount
  useEffect(() => {
    fetchRoles();
  }, []);

  // Handle custom permission change
  const handlePermissionChange = (group, checkedValues) => {
    setCustomPermissions((prev) => ({ ...prev, [group]: checkedValues }));
  };

  // Handle create custom role
  const handleCreateCustomRole = async ({ name, description, policyIds }) => {
    setCustomRoleLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      await apiClient.post(
        `${baseUrl}/roles`,
        {
          name: name,
          description: description,
          policyIds: policyIds,
        }
      );
      message.success("Custom role created");
      setShowCustomModal(false);
      setCustomRoleName("");
      setCustomRoleDesc("");
      setCustomPermissions({});
      setSelectedPolicyIds([]);
      await fetchRoles(); // Refresh roles list
    } catch (e) {
      message.error("Failed to create role");
    } finally {
      setCustomRoleLoading(false);
    }
  };

  // Handle form submit
  const handleSubmit = async (values) => {
    const selected =
      [...customRoles, ...roles].find((r) => r.id === selectedRole) || {};
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const response = await apiClient.post(
        `${baseUrl}/admin-action/create`,
        {
          actionType: "CREATE_USER",
          targetUserId: null,
          userRequest: {
            email: values.email,
            role: selected.name,
          },
        }
      );
      message.success(response.data?.message || "User created successfully!");
      navigate("/admin/users");
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to create user.");
    }
  };

  // Get policies for selected role (API response)
  const getRolePolicies = (roleId) => {
    const allRoles = [...customRoles, ...roles];
    const role = allRoles.find((r) => r.id === roleId);
    return role && role.policies ? role.policies : null;
  };

  // Fetch policies only if not already fetched
  const fetchPolicies = async () => {
    if (availablePolicies) return;
    try {
      const baseUrl = process.env.REACT_APP_AUTH_URL || "";
      const res = await apiClient.get(`${baseUrl}/policies`, {
        headers: {
          "ngrok-skip-browser-warning": "application/json",
        },
      });

      // Check if response has the expected structure with success field
      if (!res.data.success) {
        throw new Error("Failed to fetch policies - success field is false");
      }

      setAvailablePolicies(res.data.data);
    } catch (e) {
      setAvailablePolicies(null);
    }
  };

  // Fetch policies when opening custom role modal
  const handleOpenCustomRoleModal = async () => {
    await fetchPolicies();
    setShowCustomModal(true);
    setCustomRoleName("");
    setCustomRoleDesc("");
    setCustomPermissions({});
    setSelectedPolicyIds([]);
  };

  return (
    <div className={`add-user-container ${isDark ? 'dark' : ''}`}>
      <div
        className="add-user-hero"
        style={{
          background: 'linear-gradient(90deg, #3AA015 0%, #F49A24 50%, rgba(255, 0, 4, 0.61) 100%)',
          borderRadius: 18,
          padding: '2px',
          marginBottom: 32,
          position: 'relative',
        }}
      >
        <div
          style={{
            background: 'transparent',
            borderRadius: 16,
            padding: "48px 48px 40px 48px",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
            position: 'relative',
            overflow: 'hidden',
            minHeight: 180,
            width: '100%',
            height: '100%',
          }}
        >
        {/* Overlay for better text readability */}
        {isDark && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.5) 100%)',
              borderRadius: 16,
            }}
          />
        )}

        {/* Content */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Title
            level={1}
            style={{
              margin: 0,
              marginBottom: 12,
              fontWeight: 800,
              fontSize: 42,
              color: isDark ? "#ffffff" : "#1a1a1a",
              textShadow: isDark ? '0 2px 4px rgba(0, 0, 0, 0.3)' : 'none',
            }}
          >
            Create New User
          </Title>
          <Text
            style={{
              fontSize: 20,
              color: isDark ? "rgba(255, 255, 255, 0.9)" : "#4a4a4a",
              textShadow: isDark ? '0 1px 2px rgba(0, 0, 0, 0.3)' : 'none',
              fontWeight: 400,
              lineHeight: 1.4,
            }}
          >
            Quickly onboard a new user and assign them the right role and permissions
          </Text>
        </div>
        </div>
      </div>
      <Divider
        style={{
          margin: 0,
          marginBottom: 32,
          borderColor: isDark ? "#333c4a" : undefined,
        }}
      />
      <div className="form-container">
        <Row gutter={[24, 24]} align="stretch" style={{ display: "flex", width: "100%" }}>
          {/* Left Side: Form */}
          <Col
            xs={24}
            md={12}
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Card
              className="form-card"
              style={{
                height: "600px",
                display: "flex",
                flexDirection: "column",
                borderRadius: 16,
                boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
                background: isDark ? "#2a2a3e" : "#ffffff",
                overflow: "hidden",
              }}
              bodyStyle={{
                borderRadius: 16,
                padding: "24px",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <div style={{ 
                flex: 1, 
                overflowY: "auto", 
                paddingRight: "8px",
                marginRight: "-8px",
                maxHeight: "calc(100% - 80px)"
              }}>
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={handleSubmit}
                  className="add-user-form"
                >
                <Form.Item
                  name="email"
                  label="Email Address"
                  rules={[
                    { required: true, message: "Please enter email address" },
                    {
                      type: "email",
                      message: "Please enter a valid email address",
                    },
                  ]}
                >
                  <Input placeholder="Enter email address" size="large" />
                </Form.Item>

                <div style={{ marginBottom: 24 }}>
                  <Title level={5} style={{ marginBottom: 8, color: isDark ? "#ffffff" : "#1a1a1a" }}>
                    Select Role
                  </Title>
                  <Space
                    direction="vertical"
                    style={{ width: "100%" }}
                    size="large"
                  >
                    {[...customRoles, ...roles].map((role) => (
                      <div
                        key={role.id}
                        className={`role-option${selectedRole === role.id ? " selected" : ""
                          }`}
                        style={{
                          width: "100%",
                          cursor: "pointer",
                          border:
                            selectedRole === role.id
                              ? "2px solid #1890ff"
                              : "1px solid #d9d9d9",
                          background:
                            selectedRole === role.id
                              ? isDark
                                ? "#223a5f"
                                : "#e6f7ff"
                              : "#fff",
                          transition: "all 0.2s",
                          borderRadius: 8,
                          padding: "16px",
                          marginBottom: "8px",
                          boxShadow: selectedRole === role.id 
                            ? "0 2px 8px rgba(24, 144, 255, 0.15)" 
                            : "0 1px 3px rgba(0, 0, 0, 0.1)",
                        }}
                        onClick={() => setSelectedRole(role.id)}
                      >
                        <div className="role-header">
                          <span className="role-name">{role.name}</span>
                        </div>
                        <div className="role-description">
                          {role.description}
                        </div>
                      </div>
                    ))}
                  </Space>
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    style={{ marginTop: 16, width: "100%" }}
                    onClick={handleOpenCustomRoleModal}
                  >
                    Add Custom Role
                  </Button>
                </div>

              </Form>
              </div>
              
              {/* Submit Button - Fixed at bottom */}
              <div style={{ 
                padding: "16px 0 0 0", 
                borderTop: isDark ? "1px solid #3a3a4e" : "1px solid #f0f0f0",
                marginTop: "16px"
              }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  size="large"
                  icon={<CheckOutlined />}
                  loading={loading}
                  style={{ width: 180 }}
                  disabled={!selectedRole}
                  onClick={() => form.submit()}
                >
                  Create User
                </Button>
              </div>
            </Card>
          </Col>

          {/* Right Side: Permissions for Selected Role */}
          <Col
            xs={24}
            md={12}
            style={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <Card
              className="form-card"
              style={{
                height: "600px",
                display: "flex",
                flexDirection: "column",
                borderRadius: 16,
                boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.3)" : "0 4px 12px rgba(0, 0, 0, 0.08)",
                border: isDark ? "1px solid #3a3a4e" : "1px solid #000",
                background: isDark ? "#2a2a3e" : "#ffffff",
                overflow: "hidden",
              }}
              bodyStyle={{
                borderRadius: 16,
                padding: "24px",
                height: "100%",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Title level={5} style={{ marginBottom: 16, color: isDark ? "#ffffff" : "#1a1a1a" }}>
                Permissions for Selected Role
              </Title>
              {selectedRole ? (
                <div
                  style={{
                    flex: 1,
                    overflowY: "auto",
                    paddingRight: "8px",
                    marginRight: "-8px",
                    maxHeight: "calc(100% - 60px)"
                  }}
                  className="permissions-scrollable"
                >
                  {(() => {
                    const policies = getRolePolicies(selectedRole);
                    if (!policies || Object.keys(policies).length === 0) {
                      return (
                        <Text type="secondary">No permissions assigned.</Text>
                      );
                    }
                    return (
                      <Collapse defaultActiveKey={Object.keys(policies)}>
                        {Object.entries(policies).map(([group, policyList]) => (
                          <Panel
                            header={
                              group.charAt(0).toUpperCase() + group.slice(1)
                            }
                            key={group}
                          >
                            {policyList && policyList.length > 0 ? (
                              <Checkbox.Group
                                value={policyList.map((policy) => policy.id)}
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 8,
                                }}
                              >
                                {policyList.map((policy) => (
                                  <Checkbox
                                    key={policy.id}
                                    value={policy.id}
                                    disabled
                                    style={{ marginBottom: 8 }}
                                  >
                                    <span
                                      style={{ color: "#888", fontSize: 13 }}
                                    >
                                      {policy.description}
                                    </span>
                                  </Checkbox>
                                ))}
                              </Checkbox.Group>
                            ) : (
                              <Text type="secondary">No permissions</Text>
                            )}
                          </Panel>
                        ))}
                      </Collapse>
                    );
                  })()}
                </div>
              ) : (
                <Text type="secondary">
                  Select a role to view its permissions.
                </Text>
              )}
            </Card>
          </Col>
        </Row>
      </div>

      <RoleModal
        visible={showCustomModal}
        onCancel={() => setShowCustomModal(false)}
        onSubmit={handleCreateCustomRole}
        initialValues={{}}
        loading={customRoleLoading}
        availablePolicies={availablePolicies}
      />
    </div>
  );
};

export default AddUser;
